import { Delta, Range, QuillOptions } from 'quill/core';
import Quill from 'quill';
import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import 'quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import { cleanDelta } from '@/lib/quill';
import AutoLinks from 'quill-auto-links';

interface QuillEditorProps extends React.HTMLAttributes<HTMLDivElement> {
  readOnly?: boolean;
  defaultValue?: any;
  quillOptions: QuillOptions;
  onTextChange?: (delta: Delta) => void;
  onSelectionChange?: Dispatch<SetStateAction<Range | undefined>>;
  placeholder?: string;
}

const QuillEditor = forwardRef<Quill | null, QuillEditorProps>(
  (
    {
      readOnly = false,
      defaultValue,
      quillOptions,
      onTextChange,
      onSelectionChange,
      placeholder,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    useEffect(() => {
      if (ref && typeof ref === 'object' && ref.current) {
        ref.current.enable(!readOnly);
      }
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );

      Quill.register('modules/autoLinks', AutoLinks);

      const quill = new Quill(editorContainer, {
        ...quillOptions,
        modules: {
          ...quillOptions.modules,
          autoLinks: true,
        },
        placeholder,
      });

      // Expose the Quill instance via ref
      if (ref) {
        if (typeof ref === 'function') ref(quill);
        else ref.current = quill;
      }

      if (defaultValueRef.current) {
        const cleanedDelta = cleanDelta(defaultValueRef.current);
        quill.setContents(cleanedDelta);
      }

      quill.on('text-change', () => {
        const delta = quill.getContents();
        const cleanedDelta = cleanDelta(delta);
        onTextChangeRef.current?.(cleanedDelta);
      });

      quill.on('selection-change', (range: Range) => {
        onSelectionChangeRef.current?.(range);
      });

      return () => {
        if (ref && typeof ref === 'object') ref.current = null;
        container.innerHTML = '';
      };
    }, [ref, placeholder]);

    return (
      <>
        <div
          ref={containerRef}
          className={cn('h-auto', className)}
          {...props}
          id="quill-editor"
        >
          {children}
        </div>
      </>
    );
  }
);

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
